<template>
  <div class="p-5 flex flex-col">
    <p class="text-primary md:text-black font-semibold text-lg md:text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="my-5 filter-container flex justify-between"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="3" />
      <vs-col
        vs-w="8"
        class="flex justify-end"
      >
        <div class="flex flex-row gap-x-4 items-stretch">
          <div class="flex items-center w-full">
            <select-with-multiple
              v-model="filters.flf_fc_nominate"
              placeholder="Select FLF/FC Nominate"
              :options="flfFcOptions"
              style="min-width: 260px;"
              :disabled="isFlfFc"
            />
          </div>
          <div class="flex items-center">
            <select-with-multiple
              :options="vesselOptions"
              placeholder="Select Vessel"
              v-model="filters.vessel"
              style="min-width: 260px;"
            /> 
          </div>
          <div class="flex items-center">
            <div class="w-full date-picker-wrapper">
              <date-picker
                v-model="filters.month_year"
                type="month"
                format="MMM YYYY"
                value-type="MMM-YYYY"
                placeholder="Select Month"
                style="min-width: 180px;"
              />
            </div>
          </div>
          <div class="flex items-center">
            <vs-button
              type="border"
              color="primary"
              class="self-center font-semibold"
              style="min-width: 100px"
              @click="resetFilters"
            >
              Reset
            </vs-button>
          </div>
        </div>
      </vs-col>
    </vs-row>
    <div class="flex flex-col p-5 bg-white rounded-md">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-6"
        @fetch="fetchChecklistMonitoring"
        ref="tabletop"
      />
      <data-table
        class="mt-6"
        :table-header="tableHeaders"
        :table-data="tableData"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        :loading="isBusy"
        :frozen-columns="0"
        body-height="100vh"
        @sort="sort"
      >
        <template #no="{ index }">
          {{ (currentPage - 1) * pageSize + index + 1 }}
        </template>
        <template #action="{ row }">
          <div class="flex flex-row gap-x-3">
            <template>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'edit')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="exportChecklist(row)"
              >
                <img :src="require('@/assets/shipo-icon-file-download-primary.svg')">
              </div>
            </template>
          </div>
        </template>
        <template #report_submitted_date="{ row }">
          <div class="flex flex-row items-center gap-x-2">
            <p>{{ moment(row.report_submitted_date).format('DD MMM YYYY HH:mm') }}</p>
          </div>
        </template>
      </data-table>
      <table-bottom
        class="mt-6"
        @fetch="fetchChecklistMonitoring"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import SelectWithMultiple from '@/components/selectWithMultiple'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { PAGE_TITLE } from '@/fixtures/Constants'
import downloadUtils from '@/utils/downloadUtils'

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    DataTable,
    TableTop,
    TableBottom,
    DatePicker,
    SelectWithMultiple
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.fetchChecklistMonitoring()
      }
    },
    checklistMonitoring: {
      handler(val) {
        this.tableData = val || []
      }
    },
    vessel: {
      handler(val) {
        this.vesselOptions = val.map(item => ({
          text: item,
          value: item
        })) || []
      }
    },
    flfFcNominates: {
      handler(val) {
        if (val) {
          this.flfFcOptions = val
        }
      }
    },
    me: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val) {
          this.filters.flf_fc_nominate = [{ value: val.flfFcNominate.id, text: val.flfFcNominate.name }]
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      me: 'Auth/me',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      totalPage: 'Table/totalPage',
      checklistMonitoring: 'ChecklistMonitoring/checklistMonitoring',
      vessel: 'CargoStolen/vesselCargoStolen',
      checklistFile: 'ChecklistMonitoring/checklistFile',
      flfFcNominates: 'VesselLineUp/flfFcNominates'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isFlfFc() {
      return !this.isEmpty(this.me.flfFcNominate)
    }
  },
  data() {
    return {
      filters: {
        sort_by: '',
        sort_dir: '',
        month_year: this.moment().format('MMM-YYYY'),
        vessel: [],
        flf_fc_nominate: []
      },
      vesselOptions: [],
      tableHeaders: [
        { key: 'no', label: 'No', width: '50px' },
        { key: 'action', label: 'Action', width: '100px' },
        { key: 'company', label: 'Company', sortable: true },
        { key: 'vessel_name', label: 'Vessel Name', sortable: true },
        { key: 'report_submitted_date', label: 'Report Submitted Date', sortable: true },
        { key: 'reported_by', label: 'Reported By', sortable: true }
      ],
      tableData: [],
      isBusy: false,
      flfFcOptions: []
    }
  },
  mounted() {
    this.fetchChecklistMonitoring()
    this.fetchVessel()
    this.fetchFlfFcNominates()
  },
  methods: {
    async fetchChecklistMonitoring() {
      try {
        this.isBusy = true
        const apiFilters = {
          sort_dir: this.filters?.sort_dir || 'desc',
          sort_by: this.filters?.sort_by || 'company',
          page: this.currentPage || 1,
          limit: this.pageSize || 10,
          flf_fc_nominate: this.filters.flf_fc_nominate.map(item => item.value) || [],
          name_of_vessel: this.filters.vessel.map(item => item.value) || [],
          month_year: this.filters.month_year ? this.moment(this.filters.month_year).format('MM-YYYY') : null
        };
        await this.$store.dispatch('ChecklistMonitoring/fetchChecklistMonitoring', apiFilters)
        this.isBusy = false
      } catch (error) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch checklist monitoring, please try again')
      }
    },
    async fetchFlfFcNominates() {
      await this.$store.dispatch('VesselLineUp/getFlfFcNominates')
    },
    async fetchVessel() {
      try {
        await this.$store.dispatch('CargoStolen/fetchVesselCargoStolen', this.filters)
      } catch (err) {
        this.showFailedNotification(this, 'Failed to fetch vessel list, please try again')
      }
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy
      this.filters.sort_dir = sortDir
      this.fetchChecklistMonitoring()
    },
    resetFilters() {
      this.filters = {
        sort_by: '',
        sort_dir: '',
        month_year: this.moment().format('MMM-YYYY'),
        vessel: [],
        flf_fc_nominate: []
      }
      if (this.isFlfFc) {
        this.filters.flf_fc_nominate = [{ value: this.me.flfFcNominate.id, text: this.me.flfFcNominate.name }]
      }
      this.$refs.tabletop.$emit('reset-search')
      this.fetchChecklistMonitoring()
    },
    async exportChecklist(row) {
      try {
        this.isBusy = true
        const file = await this.$store.dispatch('ChecklistMonitoring/exportChecklistMonitoring', { id: row.id }, {  
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          responseType: 'blob'
        });
        if (file && file.data) {
          downloadUtils(file, `ChecklistMonitoring-${row.id}.xlsx`);
        }
        this.isBusy = false
      } catch (error) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to download export, please try again');
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-container {
  @apply w-full p-3 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply w-8;
  @apply h-8;
  @apply bg-primary;
  border-radius: 0.5rem;
}
</style>